import React from "react";
import _ from "lodash";
import { Button, IconButton, makeStyles } from "@material-ui/core";
import classNames from "classnames";
import { primaryColorLight } from "../../lib/constants";
import { useDispatch, useSelector } from "react-redux";
import { stepTwoTexts } from "../../containers/steps/stepTwo";
import { setPageNumber } from "../../epics/pageNumberEpic";
import { addCheckedTags, TAXONOMY_TAGS } from "../../epics/stepThreeEpics";
import TaxonomyData from "../../taxonomyData.json";
import MobileDialog from "../MobileDialog";
import { addKeyphrases } from "../../epics/canvasEpics";
import { HelpOutline, InfoOutlined, Replay } from "@material-ui/icons";

const useStyles = makeStyles(
  () => ({
    root: {
      display: "flex",
      flexGrow: 0,
      padding: "10px 0px",
      justifyContent: "space-evenly",
      backgroundColor: "#ffffff",
      position: "fixed",
      bottom: "0px",
      width: "100%",
      zIndex: "1"
    },
    contactButton: {
      //backgroundColor: "#e55b5e",
      "&:hover": {
        //backgroundColor: primaryColorLight
      }
    },
    hidden: {
      visibility: "hidden"
    }
  }),
  {
    name: "StepperButtons"
  }
);

const sentenceScore = (text) => ({ type: "SENTENCE_SCORE", payload: text });
const calculateTaxonomyPositions = (text, tags) => ({
  type: TAXONOMY_TAGS,
  payload: {
    text: text,
    tags: tags
  }
});

const useStepActions = (currentStep) => {
  const dispatch = useDispatch();
  const checkedCardStepOne = useSelector((state) => state.checkedCardStepOne);
  const checkedCardStepTwo = useSelector((state) => state.checkedCardStepTwo);
  const keyphraseData = useSelector((state) => state.keyphrases);
  const checkedTags = useSelector((state) => state.checkedTags);
  const taxonomyGroups = useSelector((state) => state.taxonomyGroups);
  const groups = useSelector((state) => state.groups, _.isEqual);
  const group = React.useMemo(
    () => groups?.[checkedCardStepOne + " " + checkedCardStepTwo],
    [groups, checkedCardStepOne, checkedCardStepTwo]
  );
  const taxonomyTagsAndPositions = useSelector((state) => state.taxonomyTagsAndPositions);
  const tagsAndPositions = React.useMemo(
    () => taxonomyTagsAndPositions?.[checkedCardStepOne + " " + checkedCardStepTwo],
    [taxonomyTagsAndPositions, checkedCardStepOne, checkedCardStepTwo]
  );
  switch (currentStep) {
    case 1:
      return {
        backward: () => {},
        forward: () => {
          if (checkedCardStepOne !== "") {
            const texts = [checkedCardStepOne];
            _.forEach(stepTwoTexts, (text) => texts.push(text[1]));
            dispatch(sentenceScore(texts));
            dispatch(setPageNumber(2));
          }
        }
      };
    case 2:
      return {
        backward: () => dispatch(setPageNumber(1)),
        forward: () => {
          if (checkedCardStepTwo !== "") {
            const keyphraseValues = keyphraseData[checkedCardStepOne + " " + checkedCardStepTwo];
            if (keyphraseValues !== undefined) {
              if (taxonomyGroups[checkedCardStepOne + " " + checkedCardStepTwo] === undefined) {
                const keyphrases = _.keys(keyphraseValues);
                const taxa = _.union(..._.map(TaxonomyData, (data) => data), keyphrases);
                dispatch(calculateTaxonomyPositions(checkedCardStepOne + " " + checkedCardStepTwo, taxa));
                if (_.isEmpty(checkedTags[checkedCardStepOne + " " + checkedCardStepTwo])) {
                  dispatch(addCheckedTags(checkedCardStepOne + " " + checkedCardStepTwo, keyphrases));
                }
              }
              dispatch(setPageNumber(3));
            }
          }
        }
      };
    case 3:
      return {
        backward: () => dispatch(setPageNumber(2)),
        forward: () => {
          _.map(TaxonomyData, (taxa, key) =>
            dispatch(
              addKeyphrases(
                taxa,
                _.map(taxa, (taxon) => tagsAndPositions?.[0][_.indexOf(tagsAndPositions?.[1], taxon)]),
                group ? (group[key] ? group[key] : []) : [],
                key,
                checkedCardStepOne + " " + checkedCardStepTwo
              )
            )
          );
          dispatch(setPageNumber(4));
        }
      };
    case 4:
      return {
        backward: () => dispatch(setPageNumber(3)),
        forward: () => {}
      };
    default:
      break;
  }
};

// TODO
export function StepperButtons() {
  const classes = useStyles();
  const currentStep = useSelector((state) => state.pageNumber);
  const checkedCardStepOne = useSelector((state) => state.checkedCardStepOne);
  const checkedCardStepTwo = useSelector((state) => state.checkedCardStepTwo);
  const stepActions = useStepActions(currentStep);
  const dispatch = useDispatch();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [disabled, setDisabled] = React.useState(false);

  const toggleDialog = () => setDialogOpen(!dialogOpen);

  const headingStep1 = "1. Ausgangstext wählen";
  const headingStep2 = "2. Vergleichstext wählen";
  const headingStep3 = "3. Schlüsselwörter wählen";
  const headingStep4 = "4. 3D Modell anzeigen";

  const leadingQuestion1 = "Wähle einen der vier Texte aus und gehe weiter.";
  const leadingQuestion2 = "Wähle einen der vier Texte aus und gehe weiter.";
  const leadingQuestion3 = "Wähle beliebig viele Schlüsselwörter.";
  const leadingQuestion4 = "Interagiere mit dem Modell.";

  const textStep1 =
    "In diesem Szenario ist der Ausgangstext ein beliebiges neues Dokument; aus diesem sollen mit Hilfe automatisierter Analysetechniken relevante Kernaussagen extrahiert werden.";
  const textStep2 =
    "Nun wird ein zu vergleichender Text gewählt, um Schlüsselwörter und Verwandtschaften der beiden gewählten Texte näher zu betrachten.";
  const textStep3 =
    "Die extrahierten Schlüsselwörter sind schließlich Kandidaten für die Erweiterung der bestehenden Unternehmenstaxonomie. Anhand der farblichen Markierung erkennt man, in welche Gruppe taXonomy die Schlüsselwörter einsortiert hat.";
  const textStep3a =
    "Wähle beliebig viele extrahierte Schlüsselwörter aus, um sie in der 3D-Ansicht anzusehen und einer Gruppe der bestehenden Unternehmenstaxonomie zuzuordnen und diese so zu erweitern.";
  const textStep3b =
    "In diesem Szenario umfasst die bestehende Unternehmenstaxonomie drei Gruppen. In der 3D-Ansicht wird sie komplett dargestellt.";
  const textStep4 =
    "Hier sieht man, wie nah die gewählten Schlüsselwörter und Taxa beieinander liegen und wie sie verknüpft werden könnten. Durch Klicken, und Zoomen mit dem Finger wird mit dem Modell interagiert.";

  const additionalInfos = () => {
    switch (currentStep) {
      case 1:
        return (
          <MobileDialog
            heading={headingStep1}
            onClose={toggleDialog}
            open={dialogOpen}
            text={`\n${leadingQuestion1} \n\n${textStep1}\n\n`}
          />
        );
      case 2:
        return (
          <MobileDialog
            heading={headingStep2}
            onClose={toggleDialog}
            open={dialogOpen}
            text={`\n${leadingQuestion2} \n\n${textStep2}\n\n`}
          />
        );
      case 3:
        return (
          <MobileDialog
            heading={headingStep3}
            onClose={toggleDialog}
            open={dialogOpen}
            text={`\n${leadingQuestion3} \n\n${textStep3}\n\n${textStep3a}\n\n${textStep3b}\n\n`}
          />
        );
      case 4:
        return (
          <MobileDialog
            heading={headingStep4}
            onClose={toggleDialog}
            open={dialogOpen}
            text={`\n${leadingQuestion4} \n\n${textStep4}\n\n`}
          />
        );
      default:
        break;
    }
  };

  const handleClickStart = (event) => {
    dispatch(setPageNumber(1));
  };

  return (
    <div className={classes.root}>
      {additionalInfos()}
      <Button
        classes={{ root: classNames(currentStep === 1 && classes.hidden) }}
        color={"primary"}
        variant={"contained"}
        onClick={stepActions.backward}
      >
        Zurück
      </Button>
      <Button variant={"outlined"} onClick={toggleDialog} style={{ border: "none" }}>
        <InfoOutlined />
      </Button>
      {currentStep === 4 && (
        <Button variant={"outlined"} onClick={handleClickStart} style={{ border: "none" }}>
          <Replay />
        </Button>
      )}
      {/*<IconButton>
        <HelpOutline />
      </IconButton>*/}
      {currentStep !== 4 ? (
        <Button
          color={"primary"}
          variant={"contained"}
          onClick={stepActions.forward}
          disabled={
            (currentStep === 1 && checkedCardStepOne === "") || (currentStep === 2 && checkedCardStepTwo === "")
          }
        >
          Weiter
        </Button>
      ) : (
        <Button
          color={"primary"}
          variant={"contained"}
          target={"_blank"}
          href={"https://digital-business.nea-x.de/contact"}
        >
          Kontakt
        </Button>
      )}
    </div>
  );
}
